import type { Me, Perm } from "./api/types";

/** Short local timestamp, e.g. "Jul 31, 09:42". Empty for missing/invalid input. */
// One formatter for every clock. toLocaleTimeString builds a new one on each
// call, about 0.2–0.4 ms: 70 ms for the chat list's 200 rows, which are drawn
// again on every switch of page (2026-10-19). The words are the same.
let clockFormat: Intl.DateTimeFormat | undefined;

/** Just the clock, for a bubble whose day is written on the separator above it. */
export function fmtClock(s?: string): string {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  // 24-hour, as 今天 and the team's phones: a browser in English wrote 「10:52 PM」.
  clockFormat ??= new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  return clockFormat.format(d);
}

/** A chat list's time, as WhatsApp's: today's clock, 昨天, the weekday within the week, then the date. A bare
 *  「10:52 PM」 from two days ago sat among today's rows. */
export function fmtListWhen(s?: string): string {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const days = Math.round((new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() - new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / 864e5);
  if (days <= 0) return fmtClock(s);
  if (days === 1) return "昨天";
  if (days < 7) return "周" + "日一二三四五六"[d.getDay()];
  return d.getFullYear() === now.getFullYear() ? `${d.getMonth() + 1}/${d.getDate()}` : `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
}

export function fmtTime(s?: string): string {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Whole-unit money with an optional currency prefix, e.g. "MYR 1,780". */
export function money(n?: number, cur?: string): string {
  return (cur ? cur + " " : "") + Math.round(n ?? 0).toLocaleString();
}

export function sourceLabel(s?: string): string {
  const known: Record<string, string> = { meta: "Meta", google: "Google", tiktok: "TikTok" };
  return (s && known[s]) || s || "";
}

export function can(me: Me | null, perm: Perm): boolean {
  return !!me && me.perms.includes(perm);
}

/** m:ss, for a recording's timer and a voice note's length. */
export function fmtSecs(s: number): string {
  if (!isFinite(s) || s < 0) return "0:00";
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
}

/**
 * Whether this Enter belongs to the IME, not to us.
 *
 * A Chinese keyboard commits a candidate with Enter (or with a number key while
 * the candidate list is open). If a handler acts on that Enter, the composition
 * is cancelled and what lands in the field is the raw pinyin — which is exactly
 * what the agents reported: they picked 「价格」 and the box said "jiage".
 *
 * `isComposing` is the modern signal; keyCode 229 is the same thing on the
 * browsers that predate it, and costs nothing to keep.
 */
export function imeComposing(e: { nativeEvent: KeyboardEvent; keyCode?: number }): boolean {
  return e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229 || e.keyCode === 229;
}

/** How long ago, the way a chat app says it: 刚刚 / 5 分钟前 / 3 小时前 / 昨天 / 4 天前 / 8月11日. */
export function fmtAgo(s?: string): string {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "刚刚";
  if (mins < 60) return `${mins} 分钟前`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.round(hours / 24);
  if (days === 1) return "昨天";
  if (days < 30) return `${days} 天前`;
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}

/** 今天 / 昨天 / 9月3日 — the day a list is grouped under. */
export function dayLabel(s: string): string {
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const back = Math.round((today - day) / 864e5);
  if (back === 0) return "今天";
  if (back === 1) return "昨天";
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}
